import React from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { getDealValueSource, getLegacyDealValue } from '@/utils/procurementValueUsd';

const SYSTEM_SHARE_TOOLTIP = "This campus's share of a system-wide contract, allocated by enrolment. The full contract value is recorded on the system's own deal, so it is not added again in totals.";
const PER_STUDENT_RATE_TOOLTIP = "Derived from the contract's published per-student rate multiplied by this campus's enrolment. The contract itself is recorded on the system's own deal.";
const SYSTEM_RATE_TOOLTIP = "Derived from the contract's published per-student rate multiplied by the total enrolment of the system's campuses. No contract value is published.";

function neoTooltipFor(deal) {
  const cohort = String(deal?.neo_cohort_level || '');
  if (cohort === 'systemwide_value_allocated_by_member_enrolment') return SYSTEM_SHARE_TOOLTIP;
  if (cohort === 'member_value_from_published_per_student_rate') return PER_STUDENT_RATE_TOOLTIP;
  if (cohort === 'system_value_from_published_per_student_rate') return SYSTEM_RATE_TOOLTIP;
  return NEO_ESTIMATED_TOOLTIP;
}

const NEO_ESTIMATED_TOOLTIP = "Generated by NEO's proprietary contract pricing engine, based on verified peer deals and normalized $/student/year benchmarks. This figure is an analytical estimate.";

function formatUSD(val) {
  if (val == null || isNaN(Number(val))) return '—';
  const n = Number(val);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * Value cell: deal_value_usd ?? neo_estimated_value ?? native (USD currency only for USD display).
 * Non-USD deals without deal_value_usd show native amount with currency — never mislabeled as USD.
 */
export default function DealValueDisplay({ deal, showOriginalCurrency }) {
  const source = getDealValueSource(deal);

  if (source === 'usd_actual') {
    const curr = deal.currency || '';
    const nativeVal = deal.deal_value ?? deal.contract_value ?? deal.value;
    const display =
      showOriginalCurrency && curr && curr !== 'USD' && nativeVal != null && Number(nativeVal) > 0
        ? `${curr} ${Number(nativeVal).toLocaleString()}`
        : formatUSD(deal.deal_value_usd);
    return <span className="text-gray-900">{display}</span>;
  }

  if (source === 'neo') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center gap-1.5 text-[#0d9488] cursor-help"
            tabIndex={0}
          >
            <span className="font-medium">{formatUSD(deal.neo_estimated_value)}</span>
            <HelpCircle className="w-3.5 h-3.5 text-[#0d9488] flex-shrink-0" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs">
          {neoTooltipFor(deal)}
        </TooltipContent>
      </Tooltip>
    );
  }

  if (source === 'native_no_fx') {
    const val = getLegacyDealValue(deal);
    const curr = (deal.currency || '').trim();
    if (val != null && curr) {
      return (
        <span className="text-gray-900">
          {curr} {val.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      );
    }
    return <span className="text-gray-500">—</span>;
  }

  if (source === 'legacy') {
    const val = getLegacyDealValue(deal);
    const curr = (deal.currency || '').trim();
    const showNative = (showOriginalCurrency || (curr && curr !== 'USD')) && curr;
    if (showNative) {
      return (
        <span className="text-gray-900">
          {curr} {val.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      );
    }
    return <span className="text-gray-900">{formatUSD(val)}</span>;
  }

  return null;
}
